import { Check, ChevronLeft, ChevronRight, ImageOff, Search, ShoppingCart, SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useCart } from "../context/CartContext";
import type { Product } from "../types";
import { translateCategory } from "../utils/labels";

interface CatalogResponse { data: Product[]; total: number; page: number; limit: number }
interface CategoryResponse { data: { category: string; total: number }[] }

const PAGE_SIZE = 24;

function paginationItems(page: number, pages: number) {
  const values = new Set([1, pages, page - 2, page - 1, page, page + 1, page + 2]);
  return [...values].filter(value => value >= 1 && value <= pages).sort((a, b) => a - b);
}

export function CatalogPage() {
  const { items, addItem, itemCount } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryResponse["data"]>([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = useMemo(() => paginationItems(page, totalPages), [page, totalPages]);

  useEffect(() => {
    api<CategoryResponse>("/api/catalog/categories").then(result => setCategories(result.data)).catch(() => undefined);
  }, []);

  useEffect(() => { setPage(1); }, [search, category]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true); setError("");
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (search.trim()) params.set("search", search.trim());
      if (category) params.set("category", category);
      api<CatalogResponse>(`/api/catalog?${params}`, { signal: controller.signal })
        .then(result => { setProducts(result.data); setTotal(result.total); })
        .catch(reason => { if (reason.name !== "AbortError") setError(reason.message); })
        .finally(() => setLoading(false));
    }, search ? 250 : 0);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [search, category, page]);

  function goToPage(nextPage: number) {
    setPage(Math.max(1, Math.min(totalPages, nextPage)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return <section className="section catalog-page">
    <div className="section-heading"><span className="eyebrow">Linha Holyman</span><h1>Catálogo de produtos</h1><p>Encontre rapidamente pelo nome, SKU ou código legado.</p></div>
    <div className="catalog-toolbar">
      <label className="search-box"><Search size={20} /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar produto ou código..." /></label>
      <label className="select-box"><SlidersHorizontal size={18} /><select value={category} onChange={event => setCategory(event.target.value)}><option value="">Todas as categorias</option>{categories.map(item => <option key={item.category} value={item.category}>{translateCategory(item.category)} ({item.total})</option>)}</select></label>
    </div>
    <div className="catalog-summary"><span>{loading ? "Carregando catálogo..." : `${total} produtos encontrados · Página ${page} de ${totalPages}`}</span>{itemCount > 0 && <Link to="/carrinho"><ShoppingCart size={17} /> Ver carrinho ({itemCount})</Link>}</div>
    {error && <div className="state-card error">{error}</div>}
    {!loading && !error && products.length === 0 && <div className="state-card"><ImageOff /><h3>Nenhum produto encontrado</h3><p>Tente outro nome, SKU ou código.</p></div>}
    <div className="product-grid">{products.map(product => {
      const cartQuantity = items.find(item => item.product.id === product.id)?.quantity || 0;
      return <article className="product-card" key={product.id}>
        <div className="product-image">{product.image_url ? <img src={product.image_url} alt={product.name} loading="lazy" /> : <div className="image-placeholder"><ImageOff /><span>Imagem em preparação</span></div>}</div>
        <div className="product-body"><span className="product-category">{translateCategory(product.category)}</span><h2>{product.name}</h2><div className="product-codes"><span>SKU <strong>{product.sku}</strong></span>{product.external_code && <span>Cód. <strong>{product.external_code}</strong></span>}</div><div className="product-footer"><span className="availability">{product.lead_time_days ? `Prazo estimado: ${product.lead_time_days} dias` : "Disponibilidade sob consulta"}</span>{product.show_price && product.price !== null ? <strong className="price">R$ {Number(product.price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong> : <span className="price-login">Preço após identificação</span>}</div><button className="button add-cart-button" type="button" onClick={() => addItem(product)}>{cartQuantity ? <Check size={17} /> : <ShoppingCart size={17} />}{cartQuantity ? `No carrinho (${cartQuantity}) · Adicionar mais` : "Colocar no carrinho"}</button></div>
      </article>;
    })}</div>
    {!loading && !error && totalPages > 1 && <nav className="pagination" aria-label="Paginação dos produtos"><button type="button" onClick={() => goToPage(page - 1)} disabled={page === 1}><ChevronLeft size={18} /> Anterior</button><div>{pageItems.map((number, index) => <span key={number}>{index > 0 && number - pageItems[index - 1] > 1 && <i>…</i>}<button type="button" className={number === page ? "active" : ""} aria-current={number === page ? "page" : undefined} onClick={() => goToPage(number)}>{number}</button></span>)}</div><button type="button" onClick={() => goToPage(page + 1)} disabled={page === totalPages}>Próxima <ChevronRight size={18} /></button></nav>}
  </section>;
}
